import { useState } from "react"
import "../Css/FileCard.css"

export default function FileCard({ file, onView, onDelete, onShare, owner, isShared, extraActions }) {
  const [isLoading, setIsLoading] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [shareEmail, setShareEmail] = useState("")

  const getFileIcon = (fileName) => {
    const extension = fileName.split(".").pop().toLowerCase()

    const iconMap = {
      // Documentos
      pdf: { icon: "📄", color: "#ef4444", bg: "#fee2e2" },
      doc: { icon: "📝", color: "#2563eb", bg: "#dbeafe" },
      docx: { icon: "📝", color: "#2563eb", bg: "#dbeafe" },

      // Hojas de cálculo
      xls: { icon: "📊", color: "#059669", bg: "#d1fae5" },
      xlsx: { icon: "📊", color: "#059669", bg: "#d1fae5" },
      csv: { icon: "📊", color: "#059669", bg: "#d1fae5" },

      // Presentaciones
      ppt: { icon: "📑", color: "#dc2626", bg: "#fee2e2" },
      pptx: { icon: "📑", color: "#dc2626", bg: "#fee2e2" },

      // Imágenes
      jpg: { icon: "🖼️", color: "#7c3aed", bg: "#ede9fe" },
      jpeg: { icon: "🖼️", color: "#7c3aed", bg: "#ede9fe" },
      png: { icon: "🖼️", color: "#7c3aed", bg: "#ede9fe" },
      gif: { icon: "🎭", color: "#7c3aed", bg: "#ede9fe" },
      svg: { icon: "🎨", color: "#7c3aed", bg: "#ede9fe" },

      // Videos
      mp4: { icon: "🎬", color: "#dc2626", bg: "#fee2e2" },
      mov: { icon: "🎬", color: "#dc2626", bg: "#fee2e2" },
      avi: { icon: "🎬", color: "#dc2626", bg: "#fee2e2" },

      // Audio
      mp3: { icon: "🎵", color: "#059669", bg: "#d1fae5" },
      wav: { icon: "🎵", color: "#059669", bg: "#d1fae5" },

      // Archivos comprimidos
      zip: { icon: "🗜️", color: "#64748b", bg: "#f1f5f9" },
      rar: { icon: "🗜️", color: "#64748b", bg: "#f1f5f9" },

      // Texto
      txt: { icon: "📄", color: "#64748b", bg: "#f1f5f9" },

      // Código
      js: { icon: "⚡", color: "#eab308", bg: "#fef3c7" },
      html: { icon: "🌐", color: "#ea580c", bg: "#fed7aa" },
      css: { icon: "🎨", color: "#3b82f6", bg: "#dbeafe" },

      // Default
      default: { icon: "📁", color: "#64748b", bg: "#f1f5f9" },
    }

    return iconMap[extension] || iconMap.default
  }

  const formatFileSize = (bytes) => {
    if (!bytes) return "Desconocido"
    const sizes = ["Bytes", "KB", "MB", "GB"]
    if (bytes === 0) return "0 Bytes"
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + " " + sizes[i]
  }

  const formatDate = (dateString) => {
    if (!dateString) return "Fecha desconocida"
    const date = new Date(dateString)
    const now = new Date()
    const diffTime = Math.abs(now - date)
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays === 1) return "Hoy"
    if (diffDays === 2) return "Ayer"
    if (diffDays <= 7) return `Hace ${diffDays} días`

    return date.toLocaleDateString()
  }

  const handleView = async () => {
    setIsLoading(true)
    try {
      await onView(file)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDelete = () => {
    if (window.confirm(`¿Estás seguro de que deseas eliminar "${file.fileName}"?`)) {
      onDelete(file)
    }
  }

  const handleShare = () => {
    if (shareEmail.trim() && onShare) {
      onShare(file, shareEmail.trim())
      setShareEmail("")
      setShowShareModal(false)
    }
  }

  const fileInfo = getFileIcon(file.fileName)
  const extension = file.fileName.split(".").pop().toLowerCase()

  return (
    <>
      <div className={`file-card ${isShared ? "shared-file" : ""}`}>
        {isShared && (
          <div className="shared-badge">
            <span>📤 Compartido por {owner}</span>
          </div>
        )}

        <div className="file-card-header">
          <div className="file-icon-container" style={{ backgroundColor: fileInfo.bg }}>
            <span className="file-icon" style={{ color: fileInfo.color }}>
              {fileInfo.icon}
            </span>
            <span className="file-extension">{extension.toUpperCase()}</span>
          </div>
          <div className="file-actions">
            <button className="action-btn view-btn" onClick={handleView} disabled={isLoading} title="Ver archivo">
              {isLoading ? "⏳" : "👁️"}
            </button>
            {onShare && (
              <button
                className="action-btn share-btn"
                onClick={() => setShowShareModal(true)}
                title="Compartir archivo"
              >
                📤
              </button>
            )}
            {onDelete && (
              <button className="action-btn delete-btn" onClick={handleDelete} title="Eliminar archivo">
                🗑️
              </button>
            )}
            {extraActions}
          </div>
        </div>

        <div className="file-card-body">
          <h3 className="file-name" title={file.fileName}>
            {file.fileName}
          </h3>
          <div className="file-meta">
            <span className="file-date">{formatDate(file.uploadedAt)}</span>
            {file.size && <span className="file-size">{formatFileSize(file.size)}</span>}
          </div>
        </div>

        <div className="file-card-footer">
          <div className="file-type-badge" style={{ backgroundColor: fileInfo.bg, color: fileInfo.color }}>
            {extension.toUpperCase()}
          </div>
        </div>
      </div>

      {/* Modal mejorado para compartir */}
      {showShareModal && (
        <div className="share-modal-overlay" onClick={() => setShowShareModal(false)}>
          <div className="share-modal" onClick={(e) => e.stopPropagation()}>
            <div className="share-modal-header">
              <div className="share-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                  <polyline points="16,6 12,2 8,6" />
                  <line x1="12" y1="2" x2="12" y2="15" />
                </svg>
              </div>
              <div className="share-title">
                <h3>Compartir archivo</h3>
                <p>"{file.fileName}"</p>
              </div>
              <button className="close-btn" onClick={() => setShowShareModal(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="share-modal-body">
              <div className="input-group">
                <label htmlFor="shareEmail">Correo electrónico</label>
                <div className="input-wrapper">
                  <svg
                    className="input-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                  <input
                    id="shareEmail"
                    type="email"
                    placeholder="usuario@ejemplo.com"
                    value={shareEmail}
                    onChange={(e) => setShareEmail(e.target.value)}
                    className="share-email-input"
                  />
                </div>
              </div>

              <div className="share-info">
                <div className="info-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                    <path d="M12 17h.01" />
                  </svg>
                  <span>La persona podrá ver y descargar este archivo</span>
                </div>
                <div className="info-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                  <span>El archivo se mantendrá seguro en tu nube</span>
                </div>
              </div>
            </div>

            <div className="share-modal-actions">
              <button onClick={() => setShowShareModal(false)} className="cancel-btn">
                Cancelar
              </button>
              <button onClick={handleShare} className="share-confirm-btn" disabled={!shareEmail.trim()}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                  <polyline points="16,6 12,2 8,6" />
                  <line x1="12" y1="2" x2="12" y2="15" />
                </svg>
                Compartir
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
